/**
 * Sysadmin Tenant Detail — Resource Limits tab (R5 §4.6 cap editor).
 *
 * Spec §4.6 [v1]: Token quota (in/out per month), GPU minutes, storage
 * GB, concurrent users, max documents, max custom agents, max MCP
 * connections. Soft + hard caps. Burst allowance. Auto-reset cadence.
 *
 * Live usage tiles render alongside the cap editor so an operator can
 * see "I'm at 12 of 50 GB" before they decide on a new cap.
 *
 * Persisted as a single JSONB column on organizations.resource_limits
 * (see migration bR5). Partial saves merge into the stored object so
 * an operator can edit one cap at a time without wiping others.
 */

import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Save, RotateCcw, HardDrive, Cpu, Coins, Users, FileText, Bot, Network, Zap, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { SectionCard } from '@/components/ui/section-card';
import { useTenantDetail, useUpdateTenantLimits, type TenantResourceLimits, type SoftHardCap } from '@/lib/use-tenants';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/limits')({
  component: TenantLimitsTab,
});

type CapKey =
  | 'maxStorageGb'
  | 'maxMonthlyTokensIn'
  | 'maxMonthlyTokensOut'
  | 'maxGpuMinutesMonthly'
  | 'maxConcurrentUsers'
  | 'maxDocuments'
  | 'maxCustomAgents'
  | 'maxMcpServers';

interface CapDef {
  key: CapKey;
  label: string;
  unit: string;
  icon: typeof HardDrive;
  description: string;
}

const CAPS: CapDef[] = [
  {
    key: 'maxStorageGb',
    label: 'Storage',
    unit: 'GB',
    icon: HardDrive,
    description: 'Total tenant document storage. Excludes Qdrant collection bytes (v1.1).',
  },
  {
    key: 'maxMonthlyTokensIn',
    label: 'Tokens — input',
    unit: 'tokens/mo',
    icon: Coins,
    description: 'Input tokens consumed by LLM calls per calendar month.',
  },
  {
    key: 'maxMonthlyTokensOut',
    label: 'Tokens — output',
    unit: 'tokens/mo',
    icon: Coins,
    description: 'Output tokens generated by LLM calls per calendar month.',
  },
  {
    key: 'maxGpuMinutesMonthly',
    label: 'GPU minutes',
    unit: 'min/mo',
    icon: Cpu,
    description: 'GPU wall-clock minutes per month across all inference.',
  },
  {
    key: 'maxConcurrentUsers',
    label: 'Concurrent users',
    unit: 'users',
    icon: Users,
    description: 'Active session count cap. Soft = warning banner; hard = login throttle.',
  },
  {
    key: 'maxDocuments',
    label: 'Documents',
    unit: 'docs',
    icon: FileText,
    description: 'Total documents in the knowledge base.',
  },
  {
    key: 'maxCustomAgents',
    label: 'Custom agents',
    unit: 'agents',
    icon: Bot,
    description: 'Tenant-authored agents (system agents excluded).',
  },
  {
    key: 'maxMcpServers',
    label: 'MCP connections',
    unit: 'servers',
    icon: Network,
    description: 'Active tenant MCP server installs (revoked excluded).',
  },
];

function TenantLimitsTab() {
  const { tenantId } = useParams({ from: '/sysadmin/tenants/$tenantId/limits' });
  const { data } = useTenantDetail(tenantId);
  const update = useUpdateTenantLimits();

  // Each cap held as a 2-field state so the form can render empty
  // inputs when the cap isn't yet set. Empty string = unset.
  const [caps, setCaps] = useState<Record<CapKey, { soft: string; hard: string }>>(() => makeEmptyCaps());
  const [burst, setBurst] = useState<string>('');
  const [cadence, setCadence] = useState<'monthly' | 'quarterly' | 'annual' | ''>('');

  // Hydrate from server snapshot.
  useEffect(() => {
    if (!data?.resourceLimits) return;
    const next = makeEmptyCaps();
    for (const k of CAPS.map((c) => c.key)) {
      const cap = data.resourceLimits[k];
      if (cap) {
        next[k] = { soft: String(cap.soft), hard: String(cap.hard) };
      }
    }
    setCaps(next);
    setBurst(
      typeof data.resourceLimits.burstAllowancePercent === 'number'
        ? String(data.resourceLimits.burstAllowancePercent)
        : '',
    );
    setCadence(data.resourceLimits.autoResetCadence ?? '');
  }, [data, tenantId]);

  const dirty = useMemo(() => {
    if (!data?.resourceLimits) return false;
    for (const k of CAPS.map((c) => c.key)) {
      const stored = data.resourceLimits[k];
      const current = caps[k];
      const storedSoft = stored ? String(stored.soft) : '';
      const storedHard = stored ? String(stored.hard) : '';
      if (current.soft !== storedSoft || current.hard !== storedHard) return true;
    }
    const storedBurst =
      typeof data.resourceLimits.burstAllowancePercent === 'number'
        ? String(data.resourceLimits.burstAllowancePercent)
        : '';
    if (burst !== storedBurst) return true;
    if (cadence !== (data.resourceLimits.autoResetCadence ?? '')) return true;
    return false;
  }, [caps, burst, cadence, data]);

  function reset() {
    if (!data?.resourceLimits) {
      setCaps(makeEmptyCaps());
      setBurst('');
      setCadence('');
      return;
    }
    const next = makeEmptyCaps();
    for (const k of CAPS.map((c) => c.key)) {
      const cap = data.resourceLimits[k];
      if (cap) next[k] = { soft: String(cap.soft), hard: String(cap.hard) };
    }
    setCaps(next);
    setBurst(
      typeof data.resourceLimits.burstAllowancePercent === 'number'
        ? String(data.resourceLimits.burstAllowancePercent)
        : '',
    );
    setCadence(data.resourceLimits.autoResetCadence ?? '');
  }

  /** Validates that for every filled cap, soft ≤ hard, both ≥ 0. */
  function validate(): { ok: boolean; firstError: string | null } {
    for (const c of CAPS) {
      const { soft, hard } = caps[c.key];
      // Both empty = cap unset, valid.
      if (soft === '' && hard === '') continue;
      // One empty + one filled = invalid; we ask for both.
      if (soft === '' || hard === '') {
        return { ok: false, firstError: `${c.label}: set both soft and hard, or clear both.` };
      }
      const s = Number(soft), h = Number(hard);
      if (!Number.isFinite(s) || !Number.isFinite(h) || s < 0 || h < 0) {
        return { ok: false, firstError: `${c.label}: caps must be non-negative numbers.` };
      }
      if (s > h) {
        return { ok: false, firstError: `${c.label}: soft (${s}) must be ≤ hard (${h}).` };
      }
    }
    if (burst !== '') {
      const b = Number(burst);
      if (!Number.isFinite(b) || b < 0 || b > 100) {
        return { ok: false, firstError: 'Burst allowance must be 0-100%.' };
      }
    }
    return { ok: true, firstError: null };
  }

  async function save() {
    const v = validate();
    if (!v.ok) {
      toast.error('Validation failed', { description: v.firstError ?? undefined });
      return;
    }

    // Build the patch body — only the fields the operator touched
    // get sent. The server merges in mode=merge by default.
    const body: TenantResourceLimits & { mode?: 'merge' | 'replace' } = { mode: 'merge' };
    for (const c of CAPS) {
      const { soft, hard } = caps[c.key];
      if (soft !== '' && hard !== '') {
        (body as Record<string, unknown>)[c.key] = {
          soft: Number(soft),
          hard: Number(hard),
        } satisfies SoftHardCap;
      }
    }
    if (burst !== '') body.burstAllowancePercent = Number(burst);
    if (cadence !== '') body.autoResetCadence = cadence;

    try {
      await update.mutateAsync({ id: tenantId, body });
      toast.success('Resource limits saved');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      if (msg.includes('SOFT_GT_HARD')) {
        toast.error('Soft cap exceeds hard cap', {
          description: 'Adjust the offending soft/hard pair and retry.',
        });
      } else {
        toast.error('Save failed', { description: msg });
      }
    }
  }

  if (!data) return null;
  const { stats } = data;

  return (
    <div className="space-y-5">
      <SectionCard
        title="Current usage"
        icon={Zap}
        description="Live counters. Tokens + GPU read the last 30 days. Storage is a live SUM of file_size."
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <UsageTile label="Storage" value={`${stats.storageGb.toFixed(2)} GB`} />
          <UsageTile label="Tokens in (30d)" value={stats.tokensIn30d.toLocaleString()} />
          <UsageTile label="Tokens out (30d)" value={stats.tokensOut30d.toLocaleString()} />
          <UsageTile label="GPU minutes (30d)" value={stats.gpuMinutes30d.toLocaleString()} />
          <UsageTile label="Documents" value={stats.documentCount.toLocaleString()} />
          <UsageTile label="Conversations" value={stats.conversationCount.toLocaleString()} />
          <UsageTile label="Messages" value={stats.messageCount.toLocaleString()} />
          <UsageTile label="Users" value={stats.userCount.toLocaleString()} />
        </div>
      </SectionCard>

      <SectionCard
        title="Caps editor"
        description="Soft cap = warning band. Hard cap = enforcement ceiling. Leave both empty to inherit plan defaults."
      >
        <div className="space-y-4">
          {CAPS.map((c) => (
            <CapRow
              key={c.key}
              def={c}
              softValue={caps[c.key].soft}
              hardValue={caps[c.key].hard}
              currentUsage={pickCurrentUsage(c.key, stats)}
              onSoftChange={(v) =>
                setCaps((prev) => ({ ...prev, [c.key]: { ...prev[c.key], soft: v } }))
              }
              onHardChange={(v) =>
                setCaps((prev) => ({ ...prev, [c.key]: { ...prev[c.key], hard: v } }))
              }
            />
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Burst & reset"
        description="Burst lets the hard cap be exceeded briefly. Reset cadence governs when monthly counters zero."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold">Burst allowance (%)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={burst}
              onChange={(e) => setBurst(e.target.value)}
              placeholder="0"
              className="mt-1"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Percentage above the hard cap allowed for short bursts. 0 = strict cap.
            </p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Auto-reset cadence</Label>
            <Select value={cadence || '__none'} onValueChange={(v) => setCadence(v === '__none' ? '' : (v as typeof cadence))}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="(not set)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">(not set — inherit plan default)</SelectItem>
                <SelectItem value="monthly">Monthly (1st of month)</SelectItem>
                <SelectItem value="quarterly">Quarterly (Jan/Apr/Jul/Oct 1st)</SelectItem>
                <SelectItem value="annual">Annual (Jan 1st)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </SectionCard>

      {dirty && (
        <div className="sticky bottom-0 z-10 bg-background border-t pt-3 pb-3 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            disabled={update.isPending}
            title="Discard unsaved changes"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={update.isPending}
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {update.isPending ? 'Saving…' : 'Save limits'}
          </Button>
        </div>
      )}
    </div>
  );
}

function UsageTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border p-3">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

function CapRow({
  def, softValue, hardValue, currentUsage, onSoftChange, onHardChange,
}: {
  def: CapDef;
  softValue: string;
  hardValue: string;
  currentUsage: number | null;
  onSoftChange: (v: string) => void;
  onHardChange: (v: string) => void;
}) {
  const Icon = def.icon;
  // Soft > hard? Show inline error to match the server validation.
  const s = softValue === '' ? null : Number(softValue);
  const h = hardValue === '' ? null : Number(hardValue);
  const invertedError = s !== null && h !== null && s > h;
  const partialError =
    (softValue === '' && hardValue !== '') ||
    (softValue !== '' && hardValue === '');
  // Usage progress vs hard cap (only when both filled).
  const usagePct =
    currentUsage !== null && h !== null && h > 0
      ? Math.min(100, Math.round((currentUsage / h) * 100))
      : null;

  return (
    <div className="grid grid-cols-12 gap-3 items-start py-2 border-b last:border-b-0">
      <div className="col-span-12 md:col-span-4">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {def.label}
          <span className="text-[10px] text-muted-foreground ml-1">({def.unit})</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5">{def.description}</p>
        {usagePct !== null && (
          <div className="mt-1.5">
            <Progress value={usagePct} className="h-1" />
            <p className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
              {currentUsage?.toLocaleString()} / {h?.toLocaleString()} ({usagePct}%)
            </p>
          </div>
        )}
      </div>
      <div className="col-span-6 md:col-span-3">
        <Label className="text-[11px] text-muted-foreground">Soft</Label>
        <Input
          type="number"
          min={0}
          value={softValue}
          onChange={(e) => onSoftChange(e.target.value)}
          placeholder="—"
          className={cn('mt-0.5', (invertedError || partialError) && 'border-amber-500')}
        />
      </div>
      <div className="col-span-6 md:col-span-3">
        <Label className="text-[11px] text-muted-foreground">Hard</Label>
        <Input
          type="number"
          min={0}
          value={hardValue}
          onChange={(e) => onHardChange(e.target.value)}
          placeholder="—"
          className={cn('mt-0.5', (invertedError || partialError) && 'border-amber-500')}
        />
      </div>
      <div className="col-span-12 md:col-span-2 pt-4">
        {invertedError && (
          <p className="text-[11px] text-amber-600 flex items-start gap-1">
            <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
            soft &gt; hard
          </p>
        )}
        {partialError && (
          <p className="text-[11px] text-amber-600 flex items-start gap-1">
            <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
            both or neither
          </p>
        )}
      </div>
    </div>
  );
}

function makeEmptyCaps(): Record<CapKey, { soft: string; hard: string }> {
  return {
    maxStorageGb: { soft: '', hard: '' },
    maxMonthlyTokensIn: { soft: '', hard: '' },
    maxMonthlyTokensOut: { soft: '', hard: '' },
    maxGpuMinutesMonthly: { soft: '', hard: '' },
    maxConcurrentUsers: { soft: '', hard: '' },
    maxDocuments: { soft: '', hard: '' },
    maxCustomAgents: { soft: '', hard: '' },
    maxMcpServers: { soft: '', hard: '' },
  };
}

function pickCurrentUsage(
  key: CapKey,
  stats: { storageGb: number; tokensIn30d: number; tokensOut30d: number; gpuMinutes30d: number; userCount: number; documentCount: number; messageCount: number; conversationCount: number },
): number | null {
  switch (key) {
    case 'maxStorageGb':
      return stats.storageGb;
    case 'maxMonthlyTokensIn':
      return stats.tokensIn30d;
    case 'maxMonthlyTokensOut':
      return stats.tokensOut30d;
    case 'maxGpuMinutesMonthly':
      return stats.gpuMinutes30d;
    case 'maxConcurrentUsers':
      // No concurrent-user counter on master; falls back to total users.
      return stats.userCount;
    case 'maxDocuments':
      return stats.documentCount;
    case 'maxCustomAgents':
    case 'maxMcpServers':
      // Not tracked in detail aggregator yet — render no progress bar.
      return null;
    default:
      return null;
  }
}
